import type { MouseEvent } from "react";
import { NavLink } from "react-router";
import { cn } from "../../lib/utils";
import { useSidebarNav } from "./useSidebarNav";
import type { NavItemDef } from "./useSidebarNav";

interface SidebarNavItemProps {
  item: NavItemDef;
  onNavigate?: () => void;
}

function SidebarNavItem({ item, onNavigate }: SidebarNavItemProps) {
  const { to, icon: Icon, activeIcon: ActiveIcon, label, end, onClick } = item;
  return (
    <NavLink
      to={to}
      end={end}
      onClick={(e: MouseEvent<HTMLAnchorElement>) => {
        onClick?.(e);
        onNavigate?.();
      }}
      className={({ isActive }) =>
        cn(
          "group flex items-center gap-2.5 rounded-2xl px-3.5 py-2.5 text-sm transition-all duration-150 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/40",
          isActive
            ? "bg-primary-light font-semibold text-primary shadow-2xs"
            : "font-medium text-ink-soft hover:bg-black/[0.03] hover:text-ink"
        )
      }
    >
      {({ isActive }) => {
        const Glyph = isActive && ActiveIcon ? ActiveIcon : Icon;
        return (
          <>
            <Glyph
              aria-hidden="true"
              className={cn(
                "size-4 shrink-0 transition-colors",
                isActive ? "text-primary" : "text-ink-soft group-hover:text-ink"
              )}
            />
            <span className="whitespace-nowrap">{label}</span>
          </>
        );
      }}
    </NavLink>
  );
}

interface SidebarNavProps {
  /** Called after any item is clicked — the mobile drawer closes itself. */
  onNavigate?: () => void;
}

/** Grouped nav list shared by the desktop sidebar and the mobile drawer. */
export function SidebarNav({ onNavigate }: SidebarNavProps) {
  const { navGroups } = useSidebarNav();
  return (
    <div className="flex flex-col">
      {navGroups.map((group) => (
        <div key={group.key}>
          {group.label && (
            <p className="px-3.5 pb-1 pt-4 text-[11px] font-semibold uppercase tracking-[0.06em] text-ink-faint">
              {group.label}
            </p>
          )}
          <ul className="flex flex-col gap-1">
            {group.items.map((item) => (
              <li key={item.label}>
                <SidebarNavItem item={item} onNavigate={onNavigate} />
              </li>
            ))}
          </ul>
        </div>
      ))}
    </div>
  );
}
